import React from "react";
import { Link } from "react-router-dom";
import SectionShell, { CenteredHighlight } from "./SectionShell";

export default function MissionFutureSection() {
  return (
    <SectionShell
      id="jovokep"
      testId="mission-future-section"
      eyebrow="Misszió"
      titleStart="A"
      titleAccent="jövőkép"
      hairline={false}
    >
      {/* Symbolic image — modern women's circle, warm sunlight, community ritual */}
      <figure className="my-4 md:my-6">
        <div className="portrait-frame relative max-w-xl mx-auto">
          <img
            data-testid="mission-future-image"
            src="https://customer-assets.emergentagent.com/job_tiszta-tukor/artifacts/7r0x7wjs_Modern%20N%C5%91i%20k%C3%B6r.png"
            alt="Modern női kör — közös tér, gyertyák, kapcsolódás"
            className="w-full h-auto block"
            style={{ filter: "saturate(0.98) contrast(1.02)" }}
            loading="lazy"
          />
        </div>
      </figure>

      {/* Opening pull quote — Szász-Tóth Enikő, verbatim */}
      <blockquote
        data-testid="mission-future-quote"
        className="my-8 md:my-10 mx-auto max-w-xl"
      >
        <p className="font-serif-display italic text-graphite text-[22px] sm:text-[24px] md:text-[27px] leading-[1.45] text-center">
          „A <span className="text-gold">női gyógyító erő</span>, mely újjá éled. Új
          közösségek, kolóniák alakulnak az új energetikai térben. Erőből
          megnyilvánulás, s bizonyítás vágya szertefoszlik. Versengés helyett itt az
          idő, mikor{" "}
          <span className="text-gold">NŐ, NŐT bátorít</span>.
        </p>
        <p className="font-serif-display italic text-graphite text-[22px] sm:text-[24px] md:text-[27px] leading-[1.45] text-center mt-5">
          Eme új energia jelen társadalom számára még ismeretlen, hisz a versengés
          minősége mélyen kódolt az energetikai rendszerekben. Valódi együttműködéstől
          megfosztó minőség ez, mely a közösség pusztításából éled.
        </p>
        <p className="font-serif-display italic text-graphite text-[22px] sm:text-[24px] md:text-[27px] leading-[1.45] text-center mt-5">
          Tudattalanul működő, mélyben húzódó mechanizmus, melynek egyik
          megnyilvánulása a feminizmus. Mindez csak önáltatás és erő fitogtatás, mely
          sosem hoz valódi változást. Amire szükség van, az a{" "}
          <span className="text-gold">mély tudati tisztulás</span> s életenergia
          áramlás."
        </p>
        <footer className="mt-6 text-[11px] tracking-[0.35em] uppercase text-muted-warm font-body text-center">
          — Szász-Tóth Enikő
        </footer>
      </blockquote>

      <p>
        Hiszek abban, hogy minden embernek megvan a maga helye, értéke és
        felelőssége. Amikor pedig ezek újra összekapcsolódnak, megszületik valami, ami
        nagyobb bármelyikünknél.
      </p>

      <div
        data-testid="mission-unity"
        className="my-10 md:my-12 text-center"
      >
        <Link
          to="/milyen-terbe-erkezel#egyseg"
          data-testid="mission-unity-link"
          className="font-serif-display italic text-[22px] sm:text-[24px] md:text-[27px] leading-[1.45] text-graphite hover:text-gold transition-colors duration-300 inline-block pb-1 border-b border-transparent hover:border-gold"
        >
          Az egység.
        </Link>
      </div>

      <p>
        Hiszem, hogy egy új társadalom nem attól születik meg, hogy új szabályokat
        alkotunk. Hanem attól, hogy egyre többen választjuk
        a <span className="text-gold">tudatosságot</span> a félelem helyett,
        a <span className="text-gold">felelősségvállalást</span> a hibáztatás helyett,
        az <span className="text-gold">együttműködést</span> a versengés helyett és
        a <span className="text-gold">kapcsolódást</span> az elkülönülés helyett.
        Olyan emberek építik az új társadalmat, akik képesek felelősséget vállalni
        önmagukért, kapcsolódni egymáshoz, értéket teremteni és együttműködni. Akik
        gyermekeik számára olyan közeget teremtenek, ahol nem a félelem, a versengés
        és a bizonyítás lesz az alapvető tapasztalat.
      </p>

      {/* Values box — biztonság, kölcsönös tisztelet, emberi méltóság, együttműködés */}
      <aside
        data-testid="mission-values"
        className="my-8 md:my-10 mx-auto max-w-xl bg-tinted border border-hairline px-7 md:px-10 py-8 md:py-10"
      >
        <p className="text-[11px] tracking-[0.35em] uppercase text-gold mb-5 font-body text-center">
          — Hanem
        </p>
        <p className="font-serif-display italic text-graphite text-[22px] sm:text-[24px] md:text-[27px] leading-[1.45] text-center">
          A biztonság.
          <br />
          A kölcsönös tisztelet.
          <br />
          Az együttműködés.
        </p>
      </aside>

      <p>
        Számomra ez nem csupán egy módszer. Ez egy{" "}
        <span className="text-gold">jövőkép</span>. Egy olyan küldetés, amelyhez a
        saját munkámmal, konzultációkkal, energetikai kiigazításokkal és csoportos
        foglalkozásokkal szeretnék hozzájárulni.
      </p>

      {/* Final pull quote — Szász-Tóth Enikő, the mission statement */}
      <figure
        data-testid="mission-final-quote"
        className="mt-12 md:mt-16 mb-4 mx-auto max-w-2xl text-center"
      >
        <blockquote className="font-serif-display italic text-graphite text-[22px] sm:text-[24px] md:text-[27px] leading-[1.45]">
          „Nem a gyermekeket szeretném megváltoztatni.
          <br />
          <span className="text-gold">
            Olyan világ építéséhez szeretnék hozzájárulni.
          </span>
          <br />
          Ahol természetes számukra önmaguk kibontakoztatása, a kapcsolódás és a
          tudatos, szabad teremtés."
        </blockquote>
        <figcaption className="mt-8 text-[11px] tracking-[0.35em] uppercase text-muted-warm font-body">
          — Szász-Tóth Enikő
        </figcaption>
      </figure>
    </SectionShell>
  );
}
